import { T } from '@agos/shared'
import { cn } from '@agos/ui'
import { ChevronDown } from 'lucide-react'
import { type ReactNode } from 'react'

/**
 * One section of the profile, opened on demand — FRONTEND.md §9.21.
 *
 * `ProfilePage` opened twelve queries on mount, four of them for sections a customer looks at least
 * often: order history, coupons, referrals, mini-games. Every one fired before anything was on screen,
 * and each section appeared as its own query landed, which is what made the page jump.
 *
 * A section that is shut does not fetch. That is the whole mechanism: the page passes `open` into the
 * query's `enabled`, so opening one is what asks for it.
 *
 * **The heading is always there.** Nothing moves behind a click that was not already a section
 * heading, and the count rides on the heading where one is known without asking — a customer can see
 * they have coupons before deciding to open them.
 */
export function ProfileSection({
  title,
  badge,
  open,
  onToggle,
  children,
  ...rest
}: {
  title: string
  /** A figure already known without a request — a count from another payload, say. */
  badge?: string | number | null
  open: boolean
  onToggle: () => void
  children: ReactNode
} & Record<`data-${string}`, string | boolean | undefined>) {
  return (
    <section className="rounded-2xl border border-border bg-card overflow-hidden" {...rest}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-section-toggle
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/40 transition-colors"
      >
        {/* §9.220 - a heading, not bold body text. Every section title on this page was a `span`,
          * so the whole of *Môj profil* had one `h2` on it and nothing read as bigger than anything
          * else. The heading sits *inside* the button, which is the accessible shape for a
          * collapsible section: `aria-expanded` stays on the control that opens it. */}
        <h2 className="flex-1 text-base font-bold">
          <T>{title}</T>
        </h2>
        {badge !== null && badge !== undefined && badge !== '' && (
          <span className="text-xs font-bold text-muted-foreground tabular-nums" data-section-badge>
            {badge}
          </span>
        )}
        <ChevronDown
          className={cn('w-4 h-4 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && <div className="px-4 pb-4 space-y-2">{children}</div>}
    </section>
  )
}
